import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { CollisionDetection, DragOverEvent, Modifier } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { DraggableItems } from '@/types/dragDrop';

interface DragAndDropContextType {
  activeItem: any;
  overItem: any;
  setActiveItem: Dispatch<SetStateAction<any>>;
  setModifiers: Dispatch<SetStateAction<Array<any>>>;
}

export const positionAssetNextToPointer: Modifier = ({ activatorEvent, activeNodeRect, transform }) => {
  if (activeNodeRect && activatorEvent) {
    const activatorCoordinates = getEventCoordinates(activatorEvent);

    if (!activatorCoordinates) {
      return transform;
    }

    const offsetX = 20;
    const offsetY = 20;

    return {
      ...transform,
      x: transform.x + activatorCoordinates.x - activeNodeRect.left + offsetX,
      y: transform.y + activatorCoordinates.y - activeNodeRect.top + offsetY,
    };
  }

  return transform;
};

const customCollisionDetectionAlgorithm: CollisionDetection = (args) => {
  // Check if there are any collisions with the pointer
  const pointerCollisions = pointerWithin(args);

  // Collision detection algorithms return an array of collisions
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  // While dragging assets with a pointer, avoid snapping to the nearest node
  // between rows, which can make tree drop highlighting appear jumpy.
  if (args.pointerCoordinates && args.active.data.current?.type === DraggableItems.ASSET) {
    return [];
  }

  // If there are no collisions with the pointer, return rectangle intersections so that it can be used with a keyboard events
  return closestCenter(args);
};

const DRAG_TYPE_MODIFIERS: Record<string, Array<Modifier>> = {
  asset: [positionAssetNextToPointer],
  folder: [restrictToVerticalAxis, restrictToFirstScrollableAncestor],
  album: [restrictToVerticalAxis, restrictToFirstScrollableAncestor],
};

const DragAndDropContext = createContext<DragAndDropContextType | undefined>(undefined);

export const DragAndDropProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeItem, setActiveItem] = useState<any>(null);
  const [overItem, setOverItem] = useState<any>(null);
  const [modifiers, setModifiers] = useState<Array<any>>([]);

  // Suppress hover in underlying UI while dragging a draggable item over it
  const setDraggingBodyClass = (enabled: boolean) => {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.classList.toggle('pointer-events-none', enabled);
  };

  useEffect(() => {
    return () => setDraggingBodyClass(false);
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        // Require a brief long-press so touch taps/scrolls don't immediately start the drag
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {}),
  );

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverItem(over?.data.current ?? null);
  };

  return (
    <DragAndDropContext.Provider value={{ activeItem, setActiveItem, setModifiers, overItem }}>
      <DndContext
        collisionDetection={customCollisionDetectionAlgorithm}
        modifiers={modifiers}
        onDragOver={handleDragOver}
        onDragStart={({ active }) => {
          const item = active?.data?.current;
          if (!item) {
            return;
          }

          const dragType = item.type;
          setModifiers(DRAG_TYPE_MODIFIERS[dragType] ?? []);
          setDraggingBodyClass(dragType === DraggableItems.ASSET);

          setOverItem(null);
          setActiveItem(active);
        }}
        onDragEnd={() => {
          setDraggingBodyClass(false);
          setActiveItem(null);
          setOverItem(null);
          setModifiers([]);
        }}
        onDragCancel={() => {
          setDraggingBodyClass(false);
          setActiveItem(null);
          setOverItem(null);
          setModifiers([]);
        }}
        sensors={sensors}
      >
        {children}
        {activeItem?.data.current?.type !== DraggableItems.ASSET && (
          <DragOverlay>{activeItem?.data.current?.renderDragOverlay?.({ active: activeItem })}</DragOverlay>
        )}
      </DndContext>
    </DragAndDropContext.Provider>
  );
};

export const useDragAndDrop = () => {
  const context = useContext(DragAndDropContext);
  if (!context) {
    throw new Error('useDragAndDrop must be used within a DragAndDropProvider');
  }
  return context;
};
